import React, { useState, useEffect, ReactElement, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Header from '../../Header'
import Footer from '../../Footer'
import Keyboard from '../../Keyboard'
import { setUIForwardAction, setUIBackwardsAction } from '../../../actions'
import { UI } from '../../../constants/ui'
import { setCountryFromCodeAction, setCountryToCodeAction } from '../../../actions/'
import { AppState } from '../../../reducers'
import { CustomCaretProps } from '../../CustomCaret'
import './fromTo.less'
import FooterMobile from '../../FooterMobile'
import { getMode } from '../../../hooks/mode'
import { MODE } from '../../../constants/mode'
import { FormFieldCountryInput } from '../../FormFieldCountryInput'

const mapDispatchToProps = {
  setUIForward: setUIForwardAction,
  setUIBackwards: setUIBackwardsAction,
  setCountryFromCode: setCountryFromCodeAction,
  setCountryToCode: setCountryToCodeAction,
}
type DispatchProps = typeof mapDispatchToProps

const mapStateToProps = (state: AppState) => ({
  countryFromCode: state.reservationChange.countryFrom,
  countryToCode: state.reservationChange.countryTo,
  cargoContent: state.reservationChange.cargoContent,
})

type StateProps = ReturnType<typeof mapStateToProps>
type Props = DispatchProps & StateProps

let countryFromInput: HTMLInputElement | null = null
let countryToInput: HTMLInputElement | null = null

interface CustomFromToProps {
  clearInputField: (currentInput: any) => void
  onChange: (value: string) => void
  onSelect: (option: string) => void
  onClick: () => void
  setActiveInput: (input: any) => void
  activeInput?: HTMLInputElement | null
  setShowKeyboard: (value: boolean) => void
  isKeyboardShown: boolean
  // from
  setCountryFromName: (value: string) => void
  countryFromName: string
  setIsCountryFromSelect: (value: boolean) => void
  isCountryFromSelect: boolean
  refFromNameCaret: React.RefObject<CustomCaretProps>
  // to
  setCountryToName: (value: string) => void
  countryToName: string
  setIsCountryToSelect: (value: boolean) => void
  isCountryToSelect: boolean
  refToNameCaret: React.RefObject<CustomCaretProps>
  mode: MODE
}

type FromToMobileProps = Omit<CustomFromToProps, 'setShowKeyboard' | 'isKeyboardShown' | 'refFromNameCaret' | 'refToNameCaret' | 'onClick' | 'mode'> & Omit<Props, 'setCountryFromCode' | 'setCountryToCode'>

const FromToMobile: React.FC<FromToMobileProps> = (props) => {
  const { cargoContent, setUIBackwards, onChange, onSelect, setActiveInput, activeInput, clearInputField, setUIForward } = props
  const { setCountryFromName, countryFromCode, setIsCountryFromSelect, isCountryFromSelect, countryFromName } = props
  const { setCountryToName, countryToName, countryToCode, setIsCountryToSelect, isCountryToSelect } = props

  const { t } = useTranslation()
  const [isNoFromCountry, setIsNoFromCountry] = useState(false)
  const [isNoToCountry, setIsNoToCountry] = useState(false)

  const onSubmit = () => {
    if (isCountryFromSelect && isCountryToSelect) {
      setUIForward(UI.DRIVERS_INFORMATION_AND_DOCUMENT_MOBILE)
    }
  }

  const onKeyboardDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode !== 13) {
      return
    }

    if (activeInput === countryFromInput && countryToInput) {
      countryToInput.focus()
    } else if (activeInput === countryToInput) {
      onSubmit()
    }
  }

  useEffect((): void => {
    setActiveInput(countryFromInput)
  }, [setActiveInput])

  return (
    <div className="from-to-mobile">
      <div className="from-to-mobile-wrapper">
        <div className="from-to-title" dangerouslySetInnerHTML={{ __html: t('routeOfTransport') }}></div>
        <div className="from-to-content">
          <div className="from-to-info">
            <FormFieldCountryInput
              id="from"
              label={t('countryOfOrigin')}
              sublabel={isNoFromCountry ? t('checkTheSpelling') : ''}
              onChange={onChange}
              onSelect={onSelect}
              setCountry={setCountryFromName}
              countryCode={countryFromCode}
              setIsCountrySelect={setIsCountryFromSelect}
              isCountrySelect={isCountryFromSelect}
              value={countryFromName}
              inputRef={countryFromInput}
              httpRef={(el: HTMLInputElement) => countryFromInput = el}
              onFocus={(): void => {
                setActiveInput(countryFromInput)
              }}
              isActive={activeInput === countryFromInput}
              autoFocus={!!countryFromName}
              setIsError={setIsNoFromCountry}
              clearInputField={clearInputField}
              onKeyDown={onKeyboardDown}
              errors={{ inputError: isNoFromCountry }}
            />
          </div>
          <div className="from-to-info">
            <FormFieldCountryInput
              id="to"
              label={t('finalDestinationCountry')}
              sublabel={isNoToCountry ? t('checkTheSpelling') : ''}
              onChange={onChange}
              onSelect={onSelect}
              setCountry={setCountryToName}
              countryCode={countryToCode}
              setIsCountrySelect={setIsCountryToSelect}
              isCountrySelect={isCountryToSelect}
              value={countryToName}
              inputRef={countryToInput}
              httpRef={(el: HTMLInputElement) => countryToInput = el}
              onFocus={(): void => {
                setActiveInput(countryToInput)
              }}
              isActive={activeInput === countryToInput}
              autoFocus={false}
              setIsError={setIsNoToCountry}
              clearInputField={clearInputField}
              onKeyDown={onKeyboardDown}
              errors={{ inputError: isNoToCountry }}
            />
          </div>
        </div>
        <button
          className="main-button-mobile"
          onClick={onSubmit}
          disabled={!isCountryFromSelect || !isCountryToSelect}
        >
          {t('next')}
        </button>
      </div>
      <div className="footer-mobile-container">
        <FooterMobile showPageCount back={() => (cargoContent === 'EMPTY' ? setUIBackwards(UI.CARGO_CONTENT) : setUIBackwards(UI.CARGO_WEIGHT))} />
      </div>
    </div>
  )
}

type FromToDesktopProps = CustomFromToProps & Omit<Props, 'setCountryFromCode' | 'setCountryToCode'>

const FromToDesktop: React.FC<FromToDesktopProps> = (props) => {
  const { cargoContent, setUIBackwards, onChange, onSelect, setActiveInput, activeInput, onClick, clearInputField, setUIForward, setShowKeyboard, isKeyboardShown, mode } = props
  const { setCountryFromName, countryFromCode, setIsCountryFromSelect, isCountryFromSelect, countryFromName, refFromNameCaret } = props
  const { setCountryToName, countryToName, countryToCode, setIsCountryToSelect, isCountryToSelect, refToNameCaret } = props
  const { t } = useTranslation()
  const [isNoFromCountry, setIsNoFromCountry] = useState(false)
  const [isNoToCountry, setIsNoToCountry] = useState(false)

  const isKioskMode = mode === MODE.KIOSK

  const onSubmit = () => {
    if (isCountryFromSelect && isCountryToSelect) {
      setUIForward(UI.DRIVERS_INFORMATION)
    }
  }

  const onKeyboardDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode !== 13) {
      return
    }

    if (activeInput === countryFromInput && countryToInput) {
      countryToInput.focus()
    } else if (activeInput === countryToInput) {
      onSubmit()
    }
  }

  useEffect((): void => {
    setActiveInput(countryFromInput)
  }, [setActiveInput])

  return (
    <React.Fragment>
      <Header
        showPageCount
        back={() => (cargoContent === 'EMPTY' ? setUIBackwards(UI.CARGO_CONTENT) : setUIBackwards(UI.CARGO_WEIGHT))}
      />
      <div className="from-to">
        <div className="from-to-title" dangerouslySetInnerHTML={{ __html: t('routeOfTransport') }}></div>
        <div className="from-to-content">
          <div className="from-to-info">
            <FormFieldCountryInput
              id="from"
              label={t('countryOfOrigin')}
              sublabel={isNoFromCountry ? t('checkTheSpelling') : ''}
              onChange={onChange}
              onSelect={onSelect}
              setCountry={setCountryFromName}
              countryCode={countryFromCode}
              setIsCountrySelect={setIsCountryFromSelect}
              isCountrySelect={isCountryFromSelect}
              value={countryFromName}
              inputRef={countryFromInput}
              httpRef={(el: HTMLInputElement) => countryFromInput = el}
              onFocus={(): void => {
                setActiveInput(countryFromInput)
              }}
              isActive={activeInput === countryFromInput}
              autoFocus={true}
              onClick={onClick}
              setIsError={setIsNoFromCountry}
              clearInputField={clearInputField}
              onKeyDown={onKeyboardDown}
              caretRef={refFromNameCaret}
              errors={{ inputError: isNoFromCountry }}
            />
          </div>
          <div className="from-to-info">
            <FormFieldCountryInput
              id="to"
              label={t('finalDestinationCountry')}
              sublabel={isNoToCountry ? t('checkTheSpelling') : ''}
              onChange={onChange}
              onSelect={onSelect}
              setCountry={setCountryToName}
              countryCode={countryToCode}
              setIsCountrySelect={setIsCountryToSelect}
              isCountrySelect={isCountryToSelect}
              value={countryToName}
              inputRef={countryToInput}
              httpRef={(el: HTMLInputElement) => countryToInput = el}
              onFocus={(): void => {
                setActiveInput(countryToInput)
              }}
              isActive={activeInput === countryToInput}
              autoFocus={false}
              onClick={onClick}
              setIsError={setIsNoToCountry}
              clearInputField={clearInputField}
              onKeyDown={onKeyboardDown}
              caretRef={refToNameCaret}
              errors={{ inputError: isNoToCountry }}
            />
          </div>
        </div>
        <button
          className="main-button"
          onClick={onSubmit}
          disabled={!isCountryFromSelect || !isCountryToSelect}
        >
          {t('next')}
        </button>
      </div>
      {isKioskMode && <div className="keyboard-cont">
        {isKeyboardShown && (
          <Keyboard input={activeInput || undefined} onChange={onChange} onClose={(): void => setShowKeyboard(false)} />
        )}
      </div>}
      <div className="footer-cont">{(!isKeyboardShown || !isKioskMode) && <Footer />}</div>
    </React.Fragment>
  )
}

const FromTo: React.FC<Props> = ({
  setUIForward,
  setUIBackwards,
  setCountryFromCode,
  countryFromCode,
  setCountryToCode,
  countryToCode,
  cargoContent,
}: Props): ReactElement => {
  const [countryFromName, setCountryFromName] = useState('')
  const [countryToName, setCountryToName] = useState('')
  const [activeInput, setActiveInput] = useState<HTMLInputElement | null>()
  const [isKeyboardShown, setShowKeyboard] = useState(true)
  const [isCountryFromSelect, setIsCountryFromSelect] = useState(false)
  const [isCountryToSelect, setIsCountryToSelect] = useState(false)
  const refFromNameCaret = useRef<CustomCaretProps>(null)
  const refToNameCaret = useRef<CustomCaretProps>(null)
  const mode = getMode()

  const updateCaretPosition = () => {
    if (activeInput === countryFromInput) {
      refFromNameCaret.current && refFromNameCaret.current.updateCaretPosition()
    } else if (activeInput === countryToInput) {
      refToNameCaret.current && refToNameCaret.current.updateCaretPosition()
    }
  }

  const onChange = (value: string): void => {
    if (activeInput === countryToInput) {
      setCountryToName(value)
      setIsCountryToSelect(false)
    } else if (activeInput === countryFromInput) {
      setCountryFromName(value)
      setIsCountryFromSelect(false)
    }
    updateCaretPosition()
  }

  const onClick = () => {
    setShowKeyboard(true)
    updateCaretPosition()
  }

  const onSelect = (option: string): void => {
    if (activeInput === countryToInput) {
      setCountryToCode(option)
      setIsCountryToSelect(true)
    } else if (activeInput === countryFromInput) {
      setCountryFromCode(option)
      setIsCountryFromSelect(true)
      countryToInput && countryToInput.focus()
    }
  }

  const clearInputField = (currentInput: any) => {
    setActiveInput(currentInput)
    if (currentInput === countryToInput) {
      setCountryToName('')
      setIsCountryToSelect(false)
    } else if (currentInput === countryFromInput) {
      setCountryFromName('')
      setIsCountryFromSelect(false)
    }
  }

  if (mode === MODE.MOBILE) {
    const mobileProps = { cargoContent, setUIBackwards, onChange, onSelect, setActiveInput, activeInput, clearInputField, setUIForward, setCountryFromName, countryFromCode, setIsCountryFromSelect, isCountryFromSelect, countryFromName, setCountryToName, countryToCode, setIsCountryToSelect, isCountryToSelect, countryToName }
    return <FromToMobile {...mobileProps} />
  } else {
    const desktopProps = { cargoContent, setUIBackwards, onChange, onSelect, setActiveInput, activeInput, onClick, clearInputField, setUIForward, setShowKeyboard, isKeyboardShown, setCountryFromName, countryFromCode, setIsCountryFromSelect, isCountryFromSelect, countryFromName, refFromNameCaret, setCountryToName, countryToCode, setIsCountryToSelect, isCountryToSelect, countryToName, refToNameCaret, mode }
    return <FromToDesktop {...desktopProps} />
  }

}

export default connect(mapStateToProps, mapDispatchToProps)(FromTo)
